import { FC, useCallback, useRef } from 'react'
import { LandscapeRowCollection } from '@/molecules/LandscapeRowCollection'
import { DiscoverItemInteraction } from '@/services/Analytics/ContentInteractions'
import { ContentSneakPeek } from '@/services/ProjectsService'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'
import { SneakPeekCard } from './SneakPeekCard'

interface Props {
  testId?: string
  sneakPeekTitleCollection?: ContentSneakPeek[] | undefined
  title?: string
  containerPosition?: number
}

export const SneakPeekCollection: FC<Props> = ({ testId, sneakPeekTitleCollection, title, containerPosition = 0 }) => {
  const { t } = useTranslate('watch')

  // Hover tracking refs
  const hoverStartTimeRef = useRef<Map<string, number>>(new Map())
  const impressionTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasTrackedImpression = useRef<Set<string>>(new Set())

  const createInteraction = useCallback(
    (item: ContentSneakPeek, itemIndex: number) => {
      const contentId = item?.id ?? ''
      const typename = 'ContentSpecial' // Sneak peeks are typically specials
      const imageUrl = item?.posterLandscapeCloudinaryPath ?? item?.project?.title?.image?.cloudinaryPath

      if (!contentId || !imageUrl) return null

      return new DiscoverItemInteraction({
        id: contentId,
        __typename: typename as 'ContentSpecial',
        attribution: (item as unknown as { attribution: unknown })?.attribution,
      })
        .withSurface('discover')
        .withPlacement('sneak_peeks', containerPosition, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(getCloudinaryImageUrl({ path: imageUrl }), 'landscape_display')
    },
    [containerPosition],
  )

  const validSneakPeeks = Array.isArray(sneakPeekTitleCollection) ? sneakPeekTitleCollection.filter(isDefined) : []

  if (validSneakPeeks.length === 0) {
    return null
  }

  return (
    <LandscapeRowCollection className="relative" testId={testId} title={title ? title : t('sneakPeeks', 'Sneak Peeks')}>
      {validSneakPeeks.map((item: ContentSneakPeek, itemIndex: number) => {
        if (!item) return null

        const linkModel = buildLinkModel(item)
        if (!linkModel) return null

        return (
          <SneakPeekCard
            key={item?.slug}
            item={item}
            linkModel={linkModel}
            itemIndex={itemIndex}
            hoverStartTimeRef={hoverStartTimeRef}
            impressionTimeoutRef={impressionTimeoutRef}
            hasTrackedImpression={hasTrackedImpression}
            createInteraction={createInteraction}
            t={t}
          />
        )
      })}
    </LandscapeRowCollection>
  )
}

const buildLinkModel = (item: ContentSneakPeek): LinkViewModel | null => {
  const paths = { watch: { index: '/watch' } } // Define paths locally or import from constants
  const href = `${paths.watch.index}/${item.project.slug}/episode/${item.id}`
  return {
    alt: item.subtitle,
    label: item.project.name ?? '',
    imageUrl: item.posterLandscapeCloudinaryPath ?? item.project.title?.image?.cloudinaryPath,
    linkUrl: href,
    track: {
      eventName: 'Content Link Clicked',
      payload: {
        linkUrl: href,
        projectSlug: item.project.slug,
        guid: item.id ?? item.project.id,
      },
    },
  }
}
